import React, { useState, useEffect, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Crown, Plus, Trash2, CheckCircle, XCircle, OctagonAlert } from "lucide-react";
import { 
  getClients,
  createCotacao,
  getCotacaoById,
  updateCotacao,
  getTipos,
  getEventos,
  getTableItems,
  getTables,
} from "../api";
import { AuthContext } from "../AuthContext";
import { API_URL_P, descontoMaximo, language, chavePix, nomeFantasia, cidade } from "../components/Constants";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { gerarPayloadPix } from "../utils/gerarPayloadPix";
import { gerarQrBase64 } from "../utils/gerarQrBase64";
import { createPortal } from "react-dom";


// Função para calcular o total
function calcularTotal(itens, produtos) {
  const itensArray = Array.isArray(itens) ? itens : [];
  const produtosArray = Array.isArray(produtos) ? produtos : [];
  
  return itensArray.reduce(
    (acc, item) => {
      const produto = produtosArray.find(p => p.produto_id === Number(item.produto_id));
      const preco = Number(item.preco_unitario) || Number(produto?.valor) || 0;
      const quantidade = Number(item.quantidade) || 0;
      const desconto = Number(item.desconto) || 0;
      const subtotal = preco * quantidade * (1 - desconto / 100);
      const descontoValor = preco * quantidade * (desconto / 100); // Valor do desconto para o item
      
      return {
        total_valor: acc.total_valor + subtotal,
        desconto_total: acc.desconto_total + descontoValor
      };
    },
    { total_valor: 0, desconto_total: 0 }
  );
}

export default function AddCotacao() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();  // ✅ PEGAR URL PARAMS
  const [clients, setClients] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sendmail, setSendmail] = useState(false);
  const [geratepdf, setGeratepdf] = useState(false);
  const [emailok, setEmailOk] = useState(false);
  const [isErroSalvar, setErroSalvar] = useState(false);
  const [isErroEnviar, setErroEnviar] = useState(false);
  const [showModalEntrada, setShowModalEntrada] = useState(false);
  const [valorEntrada, setValorEntrada] = useState("");
  const [resolveEntrada, setResolveEntrada] = useState(null);
  const [rejectEntrada, setRejectEntrada] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [tables, setTables] = useState([]);
  const [clientIE, setClientIE] = useState(false);

  const clienteIdFromUrl = searchParams.get("cliente");
  const cotacaoIdFromUrl = searchParams.get("cotacao");
  const isEditing = Boolean(cotacaoIdFromUrl);
  const navigate = useNavigate();

  const atendente = user.nome.charAt(0).toUpperCase() + user.nome.slice(1);

  const evento_id = localStorage.getItem("evento") || 0;

  // Formulário - AUTO-PREENCHER CLIENTE
  const [formData, setFormData] = useState({
    cotacao_id: 0,
    cliente_id: clienteIdFromUrl || "",
    usuario_id: user.sub,
    nome_atendente: atendente,
    tipo_id: "",
    tipo_descricao: "-",
    validade: new Date(new Date().setDate(new Date().getDate() + 10)).toISOString().split('T')[0],
    forma_pagamento: "Débito",
    tipo_frete: "CIF",
    transportadora: "",
    observacao: "",
    total_valor: 0,
    status: "Pendente",
    email_ok: 0
  });

  const isDisabled = geratepdf || formData.status !== "Aprovada";

  const isEditable = formData.status !== "Aprovada" && formData.status !== "Rejeitada";

  // Itens da cotação
  const [itens, setItens] = useState([
    { produto_id: "", quantidade: 1, preco_unitario: 0, desconto: 0 }
  ]);

  // Carregar dados
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [clientsData, tiposData, eventosData, tablesData] = await Promise.all([
          getClients(user.token),
          getTipos(user.token),
          getEventos(user.token),
          getTables(user.token),
        ]);

        const sortedClients = [...clientsData].sort((a, b) => b.id - a.id);
        setClients(sortedClients);

        setTipos(tiposData);

        setEventos(eventosData);

        setTables(tablesData);

        let clienteId = clienteIdFromUrl;

        // Se estiver editando
        if (isEditing) {
          const cotacaoData = await getCotacaoById(user.token, cotacaoIdFromUrl);

          clienteId = cotacaoData.cliente_id;

          setFormData({
            cotacao_id: cotacaoData.id,
            cliente_id: cotacaoData.cliente_id,
            nome_atendente: cotacaoData.nome_atendente,
            tipo_id: cotacaoData.tipo_id,
            validade: cotacaoData.validade,
            forma_pagamento: cotacaoData.forma_pagamento,
            tipo_frete: cotacaoData.tipo_frete,
            transportadora: cotacaoData.transportadora,
            observacao: cotacaoData.observacao,
            status: cotacaoData.status,
            data_pagamento: cotacaoData.data_pagamento,
            email_ok: cotacaoData.email_ok || 0
          });

          setEmailOk(cotacaoData.email_ok === 1);
          setItens(
            cotacaoData.itens.map(item => ({
              produto_id: item.produto_id,
              quantidade: item.quantidade,
              preco_unitario: item.preco_unitario,
              desconto: item.desconto
            }))
          );
        }

        // seta cliente no form (1x só)
        if (clienteId) {
          setFormData(prev => ({ ...prev, cliente_id: clienteId }));
        }

      } catch (error) {
        console.error("Erro:", error);
        setMessage({ text: "Erro ao carregar dados", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user.token, clienteIdFromUrl, isEditing, cotacaoIdFromUrl]);

  useEffect(() => {
    const loadProdutosTabela = async () => {
      if (!formData.cliente_id || !tables.length) return;

      const cliente = clients.find(c => c.id === Number(formData.cliente_id));
      if (!cliente) return;

      // 🔍 encontrar tabela correta
      const tabela = tables.find(t =>
        t.evento_id === Number(evento_id) &&
        t.tipo_id === Number(cliente.tipo_id)
      );

      if (!tabela) {
        console.warn("Tabela não encontrada");
        setProdutos([]);
        return;
      }

      try {
        const itensTabela = await getTableItems(user.token, tabela.id);

        // 🔥 aqui você já recebe produto + valor
        setProdutos(itensTabela);

      } catch (err) {
        console.error("Erro ao carregar tabela:", err);
        setProdutos([]);
      }
    };

    loadProdutosTabela();
  }, [formData.cliente_id, tables, clients, evento_id, user.token]);

  useEffect(() => {
    if (!formData.cliente_id || !clients.length) {
      setClientIE(false);
      return;
    }

    const cliente = clients.find(
      c => c.id === Number(formData.cliente_id)
    );

    // Ajuste o nome do campo conforme vem da API
    const possuiIE = !!cliente?.inscricao_estadual;

    setClientIE(possuiIE);

  }, [formData.cliente_id, clients]);

  // Adicionar item
  const addItem = () => {
    setItens([...itens, { produto_id: "", quantidade: 1, preco_unitario: 0, desconto: 0 }]);
  };

  // Remover item
  const removeItem = (index) => {
    if (itens.length > 1) {
      setItens(itens.filter((_, i) => i !== index));
    }
  };

  // Calcular o preço mínimo
  const validarPrecoMinimo = (item) => {
    const produto = produtos.find(p => p.produto_id === Number(item.produto_id));

    const precoBase = Number(produto?.valor) || 0;
    const precoUnitario = Number(item.preco_unitario) || 0;
    const desconto = Number(item.desconto) || 0;

    const precoFinal = precoUnitario * (1 - desconto / 100);
    const precoMinimo = precoBase * (1 - descontoMaximo() / 100);

    return {
      valido: precoFinal >= precoMinimo,
      precoFinal,
      precoMinimo
    };
  };

  // Função utilitária para timeout (evita travar em requisições lentas)
  async function fetchWithTimeout(resource, options = {}, timeout = 10000) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(resource, { ...options, signal: controller.signal });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      throw new Error("Falha de conexão com o servidor.");
    }
  }

  // Função de enviar cotação por email
  const handleSendEmail = async () => {
    setSendmail(true);
    setErroEnviar(false);

    try {
      // 🔥 GARANTE QUE A COTAÇÃO EXISTE E TEM ID
      let cotacaoId = formData.cotacao_id;

      if (!cotacaoId || cotacaoId === 0) {
        cotacaoId = await submitCotacao(null, { redirect: false });

        if (!cotacaoId) {
          throw new Error("Erro ao salvar cotação antes do envio");
        }
      }

      // 🔍 Cliente
      const cliente = clients.find(c => c.id === Number(formData.cliente_id));

      if (!cliente) {
        throw new Error("Selecione um cliente válido antes de enviar o e-mail.");
      }

      // 💰 Totais
      const { total_valor, desconto_total } = calcularTotal(itens, produtos);

      const tipoCliente = clients.find(
        c => Number(c.id) === Number(formData.cliente_id)
      );

      const tipoDescricao = getTipoDescricao(tipoCliente?.tipo_id) || "";
      const eventoDescricao = getEventoDescricao(Number(evento_id)) || "";

      const tipo_tabela = `${
        tipoDescricao ? tipoDescricao.charAt(0) : "N/A"
      } - ${
        eventoDescricao || "N/A"
      }`;

      // 📦 Monta payload
      const cotacaoData = {
        cliente: {
          cotacao_id: cotacaoId, // 🔥 USA O ID GARANTIDO
          cnpj: cliente.cnpj || "Não informado",
          razao_social: cliente.razao_social || "Cliente não encontrado",
          nome_fantasia: cliente.nome_fantasia || "Não informado",
          inscricao_estadual: cliente.inscricao_estadual || "Não informado",
          inscricao_municipal: cliente.inscricao_municipal || "Não informado",
          cep: cliente.cep || "Não informado",
          logradouro: cliente.logradouro || "Não informado",
          numero: cliente.numero || "Não informado",
          complemento: cliente.complemento || "Complemento não informado",
          bairro: cliente.bairro || "Não informado",
          cidade: cliente.cidade || "Não informado",
          uf: cliente.uf || "Não informado",
          telefone: cliente.telefone || "Não informado",
          telefone_secundario: cliente.telefone_secundario || "Não informado",
          email: cliente.email || "Não informado",
          email_financeiro: cliente.email_financeiro || "Não informado",
          representante: cliente.representante || "Não informado",
          cargo: cliente.cargo || "Não informado",
          contato_telefone: cliente.contato_telefone || "Não informado",
          contato_email: cliente.contato_email || "Não informado",
          observacoes: cliente.observacoes || "Nenhuma observação"
        },
        validade: formData.validade,
        status: "Enviada",
        forma_pagamento: formData.forma_pagamento,
        tipo_frete: formData.tipo_frete,
        transportadora: formData.transportadora,
        itens: itens.map(i => {
          const produto = produtos.find(p => p.produto_id === Number(i.produto_id));
          const preco = Number(i.preco_unitario) || Number(produto?.valor) || 0;
          const quantidade = Number(i.quantidade) || 0;
          const desconto = Number(i.desconto) || 0;

          return {
            ...i,
            codigo: produto?.codigo,
            produto_nome: produto?.referencia || "Produto não encontrado",
            preco_unitario: preco,
            quantidade,
            desconto,
            subtotal: preco * quantidade * (1 - desconto / 100)
          };
        }),
        total_valor,
        desconto_total,
        atendente,
        tipo: tipo_tabela,
        observacao: formData.observacao || "Nenhuma observação",
        email_ok: 1
      };

      // 📄 PDF (opcional)
      let pdfBlob = null;

      if (formData.status === "Aprovada") {
        pdfBlob = await handleGeneratePdf();
      }

      // 📡 Request
      const requestData = new FormData();
      requestData.append("cotacao", JSON.stringify(cotacaoData));
      requestData.append("email", cliente.email);

      if (pdfBlob) {
        requestData.append("arquivo", pdfBlob, `pedido_${cotacaoId}.pdf`);
      }

      const response = await fetchWithTimeout(API_URL_P + "api_send_mail.php", {
        method: "POST",
        body: requestData
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Erro ao enviar email");
      }

      const data = await response.json().catch(() => null);

      if (!data?.success) {
        throw new Error(data?.error || "Falha ao enviar email");
      }

      // 🔥 Atualiza estado e salva novamente
      const dadosParaSalvar = {
        formData: {
          ...formData,
          cotacao_id: cotacaoId,
          email_ok: 1,
          status: "Enviada",
          validade: formData.validade || new Date().toISOString().split('T')[0],
          forma_pagamento: formData.forma_pagamento || "Não informado",
          tipo_frete: formData.tipo_frete || "Não informado",
          transportadora: formData.transportadora || "Não informado",
          observacao: formData.observacao || "Nenhuma observação"
        },
        itens,
        total_valor,
        desconto_total
      };

      setEmailOk(true);

      setFormData(prev => ({
        ...prev,
        cotacao_id: cotacaoId,
        email_ok: 1,
        status: "Enviada"
      }));

      handleSetStatus("Enviada");

      await submitCotacao(dadosParaSalvar, { redirect: true });

      setMessage({
        text: "Email enviado com sucesso!",
        type: "success"
      });

    } catch (error) {
      console.error(error);

      setMessage({
        text: `Erro: ${error.message}`,
        type: "error"
      });

      setErroEnviar(true);

    } finally {
      setSendmail(false);
    }
  };

  const formatDateBR = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return isNaN(d) ? "-" : d.toLocaleDateString(language());
  };

  function parseMoedaBR(valor) {
    if (!valor) return 0;

    if (typeof valor === "number") return valor;

    return Number(
      valor
        .trim()
        .replace(/\./g, '')   // remove separador de milhar
        .replace(',', '.')    // converte decimal
    );
  }

  function solicitarValorEntrada() {
    return new Promise((resolve, reject) => {
      setShowModalEntrada(true);

      setResolveEntrada(() => resolve);
      setRejectEntrada(() => reject);
    });
  }

  function getTipoDescricao(tipo_id) {
    return tipos.find(t => t.id === tipo_id)?.descricao || "-";
  }

  function getEventoDescricao(evento_id) {
    return eventos.find(e => e.id === evento_id)?.descricao || "Nenhum Evento";
  }

  // Função para gerar o pedido em pdf
  const handleGeneratePdf = async () => {
    try {
      setGeratepdf(true);

      const doc = new jsPDF();
      let y = 14; // controle vertical dinâmico

      const lineGap = 6;

      // =========================
      // 📌 CABEÇALHO
      // =========================
      const logo = "data:image/png;base64,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"; // mantém o seu

      doc.addImage(logo, "PNG", 150, y, 42, 10);

      doc.setFontSize(18);
      doc.text(`Pedido n°${formData.cotacao_id}`, 14, y + 6);

      y += 14;

      doc.setDrawColor(200);
      doc.line(14, y, 196, y);

      y += 6;

      doc.setFontSize(10);
      doc.text(`Data: ${new Date().toLocaleDateString(language())}`, 14, y);

      y += 10;

      // =========================
      // 👤 CLIENTE
      // =========================
      const cliente = clients.find(c => c.id === Number(formData.cliente_id)) || {};

      const addSectionTitle = (title) => {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text(title, 14, y);
        doc.setFont(undefined, "normal");
        y += 6;
      };

      const addLine = (label, value, x = 14) => {
        doc.text(`${label}: ${value || "-"}`, x, y);
        y += lineGap;
      };

      addSectionTitle("Dados do Cliente");

      addLine("CNPJ", cliente.cnpj);
      addLine("Razão Social", cliente.razao_social);
      addLine("Nome Fantasia", cliente.nome_fantasia);

      // Linha dupla (IE / IM)
      doc.text(`IE: ${cliente.inscricao_estadual || "-"}`, 14, y);
      y += lineGap;

      // =========================
      // 📍 ENDEREÇO
      // =========================
      addSectionTitle("Endereço");

      addLine("CEP", cliente.cep);
      addLine("Logradouro", cliente.logradouro);

      doc.text(`Número: ${cliente.numero || "-"}`, 14, y);
      doc.text(`Complemento: ${cliente.complemento || "-"}`, 110, y);
      y += lineGap;

      doc.text(`Bairro: ${cliente.bairro || "-"}`, 14, y);
      doc.text(`Cidade: ${cliente.cidade || "-"}`, 110, y);
      y += lineGap;

      addLine("UF", cliente.uf);

      // =========================
      // 📞 CONTATO
      // =========================
      addSectionTitle("Contato");

      doc.text(`Fone: ${cliente.telefone || "-"}`, 14, y);
      doc.text(`Fone 2: ${cliente.telefone_secundario || "-"}`, 110, y);
      y += lineGap;

      addLine("Email", cliente.email);
      addLine("Email Financeiro", cliente.email_financeiro);

      doc.text(`Contato: ${cliente.representante || "-"}`, 14, y);
      doc.text(`Cargo: ${cliente.cargo || "-"}`, 110, y);
      y += lineGap;

      doc.text(`Contato Telefone: ${cliente.contato_telefone || "-"}`, 14, y);
      doc.text(`Email Contato: ${cliente.contato_email || "-"}`, 110, y);
      y += 10;

      // =========================
      // 📄 DADOS DO PEDIDO
      // =========================
      addSectionTitle("Dados do Pedido");

      const tableData = itens.map((item) => {
        const produto = produtos.find(p => p.produto_id === Number(item.produto_id));

        const preco = Number(item.preco_unitario) || Number(produto?.valor) || 0;
        const quantidade = Number(item.quantidade) || 0;
        const desconto = Number(item.desconto) || 0;

        const totalItem = preco * quantidade * (1 - desconto / 100);

        return [
          produto?.codigo,
          produto?.referencia || "Produto",
          quantidade,
          `R$ ${preco.toLocaleString(language(), {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
          `${desconto}%`,
          `R$ ${totalItem.toLocaleString(language(), {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
        ];
      });

      const { total_valor, desconto_total } = calcularTotal(itens, produtos);

      if (
        formData.forma_pagamento === "EntradaParcelas" ||
        formData.forma_pagamento === "Transferencia"
      ) {
        let valor = 0;
        let tipoPagamento = "total";

        if (formData.forma_pagamento === "EntradaParcelas") {
          try {
            const entradaInformada = await solicitarValorEntrada();

            valor = parseMoedaBR(entradaInformada);
            tipoPagamento = "da entrada";

          } catch (e) {
            console.log("Cancelado:", e);
            return; // aqui sim entra ao cancelar
          }

        } else {
          valor = parseMoedaBR(total_valor);
        }

        const payload = gerarPayloadPix(
          chavePix(),
          nomeFantasia(),
          cidade(),
          valor,
          `Pedido${formData.cotacao_id}`
        );

        const qrBase64 = await gerarQrBase64(payload);

        doc.text(`Chave Pix para pagamento ${tipoPagamento}:`, 110, y+6);
        doc.addImage(qrBase64, "PNG", 130, y+8, 25, 25);

        doc.setFontSize(7);
        doc.text(payload, 13, y + 36);
        doc.setFontSize(11);
      }

      addLine("Atendente", atendente);
      addLine("Validade", formatDateBR(formData.validade));

      const pagamento = formData.forma_pagamento === "Credito" ? "Cartão de Crédito" :
                        formData.forma_pagamento === "Débito" ? "Cartão de Débito" :
                        formData.forma_pagamento === "Transferencia" ? "Pix" :
                        formData.forma_pagamento === "Dinheiro" ? "Dinheiro" :
                        formData.forma_pagamento === "EntradaParcelas" ? "Entrada + Parcelas" : "-";
      doc.text(`Forma de pagamento: ${pagamento}`, 14, y);
      y += lineGap;
      doc.text(`Data de pagamento: ${formatDateBR(formData.data_pagamento)}`, 14, y);
      y += lineGap;

      doc.text(`Frete: ${formData.tipo_frete || "-"}`, 14, y);
      y += lineGap;
      doc.text(`Transportadora: ${formData.transportadora || "-"}`, 14, y);
      y += 10;

      // =========================
      // 📦 TABELA
      // =========================
      autoTable(doc, {
        startY: y,
        head: [["Código", "Produto", "Qtd", "Valor", "Desc", "Total"]],
        body: tableData,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [41, 128, 185] },
        margin: { left: 14, right: 14 }
      });

      // =========================
      // 💰 TOTAL
      // =========================
      const finalY = doc.lastAutoTable.finalY + 6;

      if (desconto_total > 0) {
        doc.setFontSize(10);
        doc.text(
          `Desconto: R$ ${desconto_total.toLocaleString(language(), {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
          14,
          finalY
        );
      }

      doc.setFontSize(12);
      doc.text(
        `Total: R$ ${total_valor.toLocaleString(language(), {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        14,
        finalY + 5
      );

      // =========================
      // 📝 OBS
      // =========================
      doc.setFontSize(10);
      const tipoCliente = clients.find(c => c.id === Number(formData.cliente_id));
      const tipo_tabela = getTipoDescricao(tipoCliente?.tipo_id).charAt(0) + " - " + getEventoDescricao(Number(evento_id));
      doc.text(`Tabela: ${tipo_tabela}`, 14, finalY + 11);
      doc.text("Observações:", 14, finalY + 15);

      doc.text(
        formData?.observacao || "-",
        14,
        finalY + 19,
        { maxWidth: 180 }
      );

      // =========================
      // 📎 RODAPÉ
      // =========================
      doc.setFontSize(8);
      doc.text(
        "Documento gerado automaticamente pelo sistema BES",
        14,
        290
      );

      // =========================
      // 💾 SALVAR
      // =========================
      const fileName = `pedido_${cotacaoIdFromUrl}_${cliente.cnpj}_${Date.now()}.pdf`;
      const pdfBlob = doc.output("blob");

      doc.save(fileName);
      handleSubmit();
      return pdfBlob;

    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
    } finally {
      setGeratepdf(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };

      if (name === "tipo_id") {
        const tipo = tipos.find(t => String(t.id) === String(value));
        updated.tipo_descricao = tipo?.descricao || "-";
      }

      return updated;
    });
  };

  const handleSetStatus = (novoStatus) => {
    setFormData((prev) => ({
      ...prev,
      status: novoStatus,
    }));
  };

  // ✅ FUNÇÃO CORRIGIDA PARA SEU BANCO
  const handleProductSelect = (index, produtoId) => {
    const produtoSelecionado = produtos.find(p => p.produto_id === Number(produtoId));
    
    const newItens = [...itens];
    newItens[index].produto_id = produtoId;
    
    // ✅ AUTO-PREENCHER VALOR (CAMPO 'valor' DO SEU BANCO)
    if (produtoSelecionado && produtoSelecionado.valor) {
      newItens[index].preco_unitario = parseFloat(produtoSelecionado.valor) || 0;
    }
    
    setItens(newItens);
  };

  const handleItemChange = (index, field, value) => {
    const newItens = [...itens];
    newItens[index][field] = value;
    setItens(newItens);
  };

  // Salvar cotação COM SPINNER
  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const dados = {
      formData: { ...formData },
      itens: [...itens]
    };

    const resultado = await submitCotacao(dados, { redirect: false });

    // ✔️ só redireciona se salvou com sucesso
    if (resultado) {
      setTimeout(() => {
        navigate("/listcotacoes");
      }, 800);
    }
  };

  const submitCotacao = async (dados = null, options = {}) => {
    setSubmitting(true);
    setErroSalvar(false);
    const { redirect = true } = options;

    try {
      // Dados base
      const fd = dados?.formData || formData;
      const its = dados?.itens || itens;
      const total_valor =
        dados?.total_valor ?? calcularTotal(its, produtos).total_valor;

      const itensValidos = its.filter(item => item.produto_id);

      // Validações
      if (!fd.cliente_id) {
        setMessage({ text: "Selecione um cliente", type: "error" });
        return null;
      }

      if (itensValidos.length === 0) {
        setMessage({ text: "Adicione pelo menos um item", type: "error" });
        return null;
      }

      // Payload
      const cotacaoPayload = {
        ...fd,
        total_valor,
        itens: itensValidos,
        email_ok: fd.email_ok ? 1 : 0
      };

      let novoId;

      // 🔥 CREATE / UPDATE
      if (cotacaoPayload.cotacao_id) {
        await updateCotacao(user.token, {
          id: cotacaoPayload.cotacao_id, // ✅ ID correto
          ...cotacaoPayload
        });

        novoId = cotacaoPayload.cotacao_id;
      } else {
        const response = await createCotacao(user.token, cotacaoPayload);

        novoId = response?.id || response?.data?.id;

        if (!novoId) {
          throw new Error("API não retornou o ID da cotação");
        }
      }

      // 🔥 Atualiza o state com o ID REAL
      setFormData(prev => ({
        ...prev,
        cotacao_id: novoId
      }));

      setMessage({
        text: "Cotação salva com sucesso!",
        type: "success"
      });

      if (redirect) {
        setTimeout(() => {
          window.location.href = "/listcotacoes";
        }, 1500);
      }

      return novoId; // 🔥 AGORA RETORNA O ID

    } catch (error) {
      console.error("ERRO AO SALVAR:", error.response || error);

      setMessage({
        text: `Erro: ${error.response?.data?.message || error.message}`,
        type: "error"
      });

      setErroSalvar(true);
      return null;

    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="dashboard-container">
          <div className="user-card">
            <div className="spinner"></div>
            <p>Carregando cotação...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card">
          {/* HEADER - MOSTRAR CLIENTE AUTO-SELECIONADO */}
          {isEditing ? (
            <h3><Crown size={20} /> Editar Cotação n° {formData.cotacao_id}</h3>
          ) : (
            <h3><Crown size={20} /> Nova Cotação</h3>
          )}

          <div className="nome-evento">
            <p style={{ 
              display: 'flex',
              alignItems: 'center'
            }}><strong>
              {getEventoDescricao(Number(evento_id))}
              </strong>
            </p>
          </div>

          <div className="icone-status">
            <p style={{ 
              color: emailok ? '#28a745' : '#dc3545',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {emailok ? (
                <>
                  <CheckCircle size={16} fill="#28a745" color="#fff" />
                  E-mail enviado
                </>
              ) : (
                <>
                  <XCircle size={16} fill="#dc3545" color="#fff" />
                  E-mail não enviado
                </>
              )}
            </p>
          </div>

          {/* MODAL VALOR DE ENTRADA */}
          {showModalEntrada &&
            createPortal(
              <div className="modal-overlay">
                <div className="modal">
                  <h3>Informe o valor de entrada</h3>

                  <input
                    inputMode="numeric"
                    value={valorEntrada}
                    onChange={(e) => setValorEntrada(e.target.value)}
                  />

                  <div className="modal-actions">
                    <button
                      className="btn btn-cancel"
                      onClick={() => {
                        rejectEntrada("cancelado");
                        setShowModalEntrada(false);
                      }}
                    >
                      Cancelar
                    </button>

                    <button
                      className="btn btn-confirm"
                      onClick={() => {
                        resolveEntrada(valorEntrada);
                        setShowModalEntrada(false);
                      }}
                    >
                      Confirmar
                    </button>
                  </div>
                </div>
              </div>,
              document.getElementById("modal-root")
            )
          }

          {/* MENSAGEM */}
          {message.text && (
            <div className={`error-msg ${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* CLIENTE - AUTO-SELECIONADO */}
            <div className="form-group">
              <label className="labels">Cliente *</label>
              <select
                name="cliente_id"
                value={formData.cliente_id}
                onChange={handleInputChange}
                required
                disabled={isEditing}
              >
                <option value="">Selecione um cliente</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.razao_social} ({client.cnpj})
                  </option>
                ))}
              </select>
            </div>

            {!clientIE && (
              <div className="form-group">
                <OctagonAlert size={16} color="#dc3545" />
                <small
                  style={{
                    color: "#dc3545",
                    margin: "0",
                    marginLeft: "5px"
                  }}
                >
                  Aviso de DIFAL
                </small>
              </div>
            )}

            <div className="form-group">
              <label className="labels">Atendente: {formData.nome_atendente || "-"} | Tabela: {
                  getTipoDescricao(
                    clients.find(c => c.id === Number(formData.cliente_id))?.tipo_id
                  )
                }</label>
            </div>

            {/* ITENS - SEÇÃO COM TÍTULO */}
            <h4 style={{ color: 'var(--text_color-secondary)', margin: '0' }}>
              Itens da Cotação
            </h4>

            {itens.map((item, index) => {
              const produtoSelecionado = produtos.find(p => p.produto_id === Number(item.produto_id));
              const validacao = validarPrecoMinimo(item);
              
              return (
                <div key={index} className="item-row">
                  {/* PRODUTO */}
                  <div className="form-group item-input">
                    <label>Produto</label>
                    <select
                      value={item.produto_id}
                      onChange={(e) => handleProductSelect(index, e.target.value)}
                      disabled={!isEditable}
                      required
                    >
                      <option value="">Selecione um produto</option>
                      {produtos.map(produto => (
                        <option key={produto.produto_id} value={produto.produto_id}>
                          {produto.codigo} - {produto.referencia}
                        </option>
                      ))}
                    </select>
                    {produtoSelecionado && (
                      <small style={{ 
                        color: 'var(--text_color-secondary)', 
                        display: 'block', 
                        fontSize: '0.8rem',
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        {produtoSelecionado.descricao}
                      </small>
                    )}
                  </div>

                  {/* QUANTIDADE */}
                  <div className="form-group item-input">
                    <label>Qtd</label>
                    <input
                      type="number"
                      value={item.quantidade}
                      onChange={(e) => handleItemChange(index, 'quantidade', parseFloat(e.target.value) || 0)}
                      disabled={!isEditable}
                      min="1"
                      step="1"
                    />
                    {produtoSelecionado && (
                      <small style={{ 
                        color: 'var(--text_color-secondary)', 
                        display: 'block', 
                        fontSize: '0.8rem',
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        .......................................
                      </small>
                    )}
                  </div>

                  {/* PREÇO */}
                  <div className="form-group item-input">
                    <label>R$ Unit.</label>
                    <input
                      type="number"
                      value={item.preco_unitario}
                      onChange={(e) => handleItemChange(index, 'preco_unitario', parseFloat(e.target.value) || 0)}
                      disabled={!isEditable}
                      min="0"
                      step="0.01"
                      style={{ 
                        background: !validacao.valido ? '#f8d7da' : '#d4edda'
                      }}
                    />
                    {!validacao.valido && (
                      <small style={{ color: '#dc3545' }}>
                        Preço final: R$ {validacao.precoFinal.toFixed(2).replace('.', ',')} <br/>
                        Mínimo permitido: R$ {validacao.precoMinimo.toFixed(2).replace('.', ',')}
                      </small>
                    )}
                    {produtoSelecionado && (
                      <small style={{ 
                        color: 'var(--text_color-secondary)', 
                        display: 'block', 
                        fontSize: '0.8rem',
                        marginTop: '2px'
                      }}>
                        Valor: R$ {parseFloat(produtoSelecionado.valor || 0).toFixed(2).replace('.', ',')}
                      </small>
                    )}
                  </div>

                  {/* DESCONTO */}
                  <div className="form-group item-input">
                    <label>Desc %</label>
                    <input
                      type="number"
                      value={item.desconto}
                      onChange={(e) => handleItemChange(index, 'desconto', parseFloat(e.target.value) || 0)}
                      disabled={!isEditable}
                      min="0"
                      max={descontoMaximo()}
                    />
                    {produtoSelecionado && (
                      <small style={{ 
                        color: 'var(--text_color-secondary)', 
                        display: 'block', 
                        fontSize: '0.8rem',
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        .......................................
                      </small>
                    )}
                  </div>

                  {/* BOTÃO REMOVER */}
                  <div className="item-actions">
                    {itens.length > 1 && (
                      <button 
                        type="button" 
                        className="btn-danger btn-small" 
                        disabled={!isEditable}
                        onClick={() => removeItem(index)}
                        style={{
                          backgroundColor: !isEditable ? "#ccc" : "",
                          color: !isEditable ? "#666" : "",
                          cursor: !isEditable ? "not-allowed" : "pointer"
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            <button type="button" className="btn-primary add-item-btn"
              disabled={!isEditable}
              onClick={addItem}
              style={{
                backgroundColor: !isEditable ? "#ccc" : "",
                color: !isEditable ? "#666" : "",
                cursor: !isEditable ? "not-allowed" : "pointer"
              }}
            >
              <Plus size={16} /> Adicionar Item
            </button>

            {/* TOTAL */}
            <div className="total-row">
              {calcularTotal(itens, produtos).desconto_total > 0 && (
                <strong style={{ marginLeft: '1rem', color: '#d9534f' }}>
                  Desconto Total: R$ {calcularTotal(itens, produtos).desconto_total.toLocaleString(language(), {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}<br/>
                </strong>
              )}
              <strong>Total: R$ {calcularTotal(itens, produtos).total_valor.toLocaleString(language(), {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}</strong>
            </div>

            {/* CONFIGURAÇÕES */}
            <div className="form-row">
              <div className="form-group">
                <label className="labels">Validade *</label>
                <input
                  type="date"
                  name="validade"
                  value={formData.validade}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="labels">Forma de Pagamento *</label>
                <select name="forma_pagamento" value={formData.forma_pagamento} onChange={handleInputChange}>
                  <option value="Debito">Cartão de Débito</option>
                  <option value="Credito">Cartão de Crédito</option>
                  <option value="Transferencia">PIX</option>
                  <option value="Dinheiro">Dinheiro</option>
                  <option value="EntradaParcelas">Entrada + Parcelas</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="labels">Tipo de Frete *</label>
                <select name="tipo_frete" value={formData.tipo_frete} onChange={handleInputChange}>
                  <option value="CIF">CIF</option>
                  <option value="CIFR">CIF Redespacho SP</option>
                  <option value="FOB">FOB</option>
                </select>
              </div>
              <div className="form-group">
                <label className="labels">Transportadora</label>
                <input
                  type="text"
                  name="transportadora"
                  value={formData.transportadora}
                  onChange={handleInputChange}
                  placeholder="Nome da transportadora"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="labels">Status</label>
                <select name="status" value={formData.status || "Pendente"} onChange={handleInputChange}>
                  <option value="Pendente">Pendente</option>
                  <option value="Enviada">Enviada</option>
                  <option value="Autorizada" disabled>Autorizada</option>
                  <option value="Aprovada">Aprovada</option>
                  <option value="Rejeitada">Rejeitada</option>
                </select>
              </div>
              <div className="form-group">
                <label className="labels">Data do Pagamento</label>
                <input
                  type="date"
                  name="data_pagamento"
                  value={formData.data_pagamento || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* OBSERVAÇÃO */}
            <div className="form-group">
              <label className="labels">Observação</label>
              <textarea
                name="observacao"
                value={formData.observacao}
                onChange={handleInputChange}
                rows="3"
                placeholder="Observações adicionais..."
              />
            </div>

            {/* BOTÃO PRINCIPAL */}
            <div className="form-group">
              {isErroSalvar ? (
                <small style={{ 
                  color: '#dc3545',
                  display: 'block', 
                  fontSize: '0.8rem',
                  marginTop: '2px',
                  marginBottom: '-15px'
                }}>
                  Erro ao salvar. Verifique os dados acima.
                </small>
              ) : ("")}
              <button
                type="submit" 
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner" style={{ marginRight: '0.5rem', width: '18px', height: '18px' }} />
                    {" "}Salvando...
                  </>
                ) : (
                  isEditing ? "Salvar Alterações" : "Criar Cotação"
                )}
              </button>

              {isErroEnviar ? (
                <small style={{ 
                  color: '#dc3545',
                  display: 'block', 
                  fontSize: '0.8rem',
                  marginTop: '2px',
                  marginBottom: '-15px'
                }}>
                  Erro ao enviar email. Verifique os dados acima.
                </small>
              ) : ("")}
              <button type="button" className="btn-primary" onClick={handleSendEmail} disabled={sendmail}>
                {sendmail ? (
                  <>
                    <span className="spinner" style={{ marginRight: '0.5rem', width: '18px', height: '18px' }} />
                    {" "}Enviando...
                  </>
                ) : ("Enviar Cotação por E-mail")}
              </button>

              {isDisabled ? (
                <small style={{ 
                  color: 'var(--text_color-secondary)', 
                  display: 'block', 
                  fontSize: '0.8rem',
                  marginTop: '2px',
                  marginBottom: '-15px'
                }}>
                  Somente disponível quando status aprovado.
                </small>
              ) : ("")}

              <button
                type="button"
                className="btn-primary"
                onClick={handleGeneratePdf}
                disabled={isDisabled}
                style={{
                  backgroundColor: isDisabled ? "#ccc" : "",
                  color: isDisabled ? "#666" : "",
                  cursor: isDisabled ? "not-allowed" : "pointer"
                }}
              >
                {geratepdf ? (
                  <>
                    <span className="spinner" style={{ marginRight: '0.5rem', width: '18px', height: '18px' }} />
                    {" "}Gerando PDF...
                  </>
                ) : ("Gerar pedido em PDF")}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}
